import { useCallback, useEffect, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"
import { getQuickCreateInitialForm } from "../components/ClinicalHistoryHelpers"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function useClinicalHistoryLauncher({
  clinicId,
  patientId,
  isReadOnly,
  readOnlyReason,
  navigate,
}) {
  const [launcherQuery, setLauncherQuery] = useState("")
  const [launcherLoading, setLauncherLoading] = useState(false)
  const [launcherPatients, setLauncherPatients] = useState([])

  const [showQuickCreate, setShowQuickCreate] = useState(false)
  const [quickCreateForm, setQuickCreateForm] = useState(getQuickCreateInitialForm())
  const [creatingPatient, setCreatingPatient] = useState(false)

  const resetQuickCreateForm = useCallback(() => {
    setQuickCreateForm(getQuickCreateInitialForm())
  }, [])

  const loadLauncherPatients = useCallback(async (searchText = "") => {
    try {
      if (!clinicId) {
        setLauncherPatients([])
        return
      }

      const normalizedQuery = String(searchText || "").trim()

      if (normalizedQuery.length < 2) {
        setLauncherPatients([])
        return
      }

      setLauncherLoading(true)

      const params = new URLSearchParams({
        clinicId,
        q: normalizedQuery,
        limit: "12",
      })

      const res = await apiFetch(
        `${API_BASE}/clinical-history/launcher?${params.toString()}`
      )
      const data = await res.json().catch(() => [])

      if (!res.ok) {
        setLauncherPatients([])
        return
      }

      setLauncherPatients(Array.isArray(data) ? data : [])
    } catch {
      setLauncherPatients([])
    } finally {
      setLauncherLoading(false)
    }
  }, [clinicId])

  const openPatientHistory = useCallback((patient) => {
    const params = new URLSearchParams()
    params.set("patientId", patient.id)
    params.set("clinicId", clinicId)
    navigate(`/clinical-history?${params.toString()}`)
  }, [clinicId, navigate])

  const handleCreatePatient = useCallback(async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason)
        return
      }

      if (!quickCreateForm.firstName.trim()) {
        alert("Escribe el nombre.")
        return
      }

      if (!quickCreateForm.lastName.trim()) {
        alert("Escribe el apellido.")
        return
      }

      setCreatingPatient(true)

      const res = await apiFetch(`${API_BASE}/patients`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: quickCreateForm.firstName.trim(),
          lastName: quickCreateForm.lastName.trim(),
          phone: quickCreateForm.phone?.trim() || undefined,
          email: quickCreateForm.email?.trim() || undefined,
          birthDate: quickCreateForm.birthDate || undefined,
        }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo crear el paciente.")
        return
      }

      setShowQuickCreate(false)
      resetQuickCreateForm()
      await loadLauncherPatients(launcherQuery)

      if (data?.id) {
        openPatientHistory({ id: data.id })
      }
    } catch {
      alert("Error al crear paciente.")
    } finally {
      setCreatingPatient(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    quickCreateForm,
    resetQuickCreateForm,
    loadLauncherPatients,
    launcherQuery,
    openPatientHistory,
  ])

  useEffect(() => {
    if (patientId) return
    if (!clinicId) return

    const timeout = setTimeout(() => {
      loadLauncherPatients(launcherQuery)
    }, 250)

    return () => clearTimeout(timeout)
  }, [launcherQuery, clinicId, patientId, loadLauncherPatients])

  return {
    launcherQuery,
    setLauncherQuery,
    launcherLoading,
    launcherPatients,
    showQuickCreate,
    setShowQuickCreate,
    quickCreateForm,
    setQuickCreateForm,
    creatingPatient,
    resetQuickCreateForm,
    loadLauncherPatients,
    openPatientHistory,
    handleCreatePatient,
  }
}
